import { useMemo, useState } from 'react';
import { Option } from '@/components/common/ReactSelect5';
import useStates from '@/hooks/api/v2/useStates';
import {
  CorrelationCategory, Filters, GRADE_ORDER, NO_FILTER,
} from '../types';

interface UseCandidateFiltersParams {
  categories: CorrelationCategory[];
  initialCategory: string;
}

/**
 * The Add panel's own filter state. It can't reuse useCorrelationFilters: that one derives its option
 * lists from the rows on screen and narrows them in JS. Here the pool is the whole framework, so the
 * options come from elsewhere and the narrowing happens in SQL.
 */
export default function useCandidateFilters({ categories, initialCategory }: UseCandidateFiltersParams) {
  const [filters, setFilters] = useState<Filters>({
    category: initialCategory, grade: NO_FILTER, search: NO_FILTER, state: NO_FILTER,
  });

  const selectedCategory = categories.find(category => category.value === filters.category) ?? null;
  const showState = Boolean(selectedCategory?.has_state_scoping);
  const showGrade = Boolean(selectedCategory?.has_grade_scoping);

  const { data: states } = useStates({
    enabled: showState,
    only: ['id', 'name'],
    params: { has_edgate_data: true },
  });

  const stateOptions: Option[] = useMemo(() => (
    (states ?? []).map(({ id, name }) => ({ label: name, value: String(id) }))
  ), [states]);

  // Every grade the enum has, not only the ones the item is already tagged with.
  const gradeOptions: Option[] = useMemo(() => (
    GRADE_ORDER.map(grade => ({ label: grade, value: grade }))
  ), []);

  const setFilter = (name: keyof Filters, value: string) => {
    setFilters((current) => {
      // A new category brings its own states and grades; keeping the old ones would filter to nothing.
      if (name === 'category') return { ...current, category: value, grade: NO_FILTER, state: NO_FILTER };

      return { ...current, [name]: value };
    });
  };

  const clearSearch = () => setFilter('search', NO_FILTER);

  return {
    clearSearch, filters, gradeOptions, setFilter, showGrade, showState, stateOptions,
  };
}
